/**
 * A stable colour derived from a name.
 *
 * Originally the menu grid's answer to a dish with no photograph. The grid has
 * since retired — at 150 photoless dishes out of 278 the tints stopped being a
 * treatment and became the look of the menu — but the same stable-colour trick
 * still earns its place at small sizes, on cart thumbnails and the "order
 * again" tiles, where there is nothing else to identify a line by.
 *
 * Two requirements pull against each other. The tint must be stable — the same
 * dish is the same colour on every device and every visit, so a regular starts
 * recognising the ftira before they read the word. And no two adjacent tiles
 * may share a tint, or a grid of imageless dishes reads as one flat block.
 *
 * A hash gives the first; a shift when a tile collides with the one before it
 * gives the second, at the cost of that one dish being off-hash in that one
 * grid. Stability is the property worth keeping, so the shift is applied to the
 * later tile and never propagated backwards.
 */
const PJ_TILE_TINT = ['stone', 'sage', 'sky', 'blush', 'sand'] as const

type PjTileTint = (typeof PJ_TILE_TINT)[number]

/**
 * FNV-1a. Chosen because it is stable across engines and platforms — the
 * Flutter build has to produce the same colour for the same dish, and anything
 * relying on a runtime's string hashing would not.
 */
const hash = (value: string): number => {
  let result = 0x811c9dc5

  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index)
    result = Math.imul(result, 0x01000193) >>> 0
  }

  return result
}

const tintFor = (name: string): PjTileTint => PJ_TILE_TINT[hash(name) % PJ_TILE_TINT.length]

/**
 * Tints for an ordered run of dishes, with adjacent collisions shifted by one.
 * Pass the whole section: a tile cannot solve this alone because the answer
 * depends on its neighbour.
 */
const assignTileTints = (names: string[]): PjTileTint[] =>
  names.reduce<PjTileTint[]>((assigned, name, index) => {
    const wanted = tintFor(name)
    const previous = assigned[index - 1]

    assigned.push(wanted === previous ? PJ_TILE_TINT[(PJ_TILE_TINT.indexOf(wanted) + 1) % PJ_TILE_TINT.length] : wanted)

    return assigned
  }, [])

export { assignTileTints, PJ_TILE_TINT, tintFor }
export type { PjTileTint }
